import { observer } from 'mobx-react-lite';
import { useMainContext } from '../../hooks/useMainContext';
import Button from '../Globle/Button';
import { IoIosArrowBack } from 'react-icons/io';
import { message, Modal } from 'antd';
import { FaRegCircleCheck } from 'react-icons/fa6';
import { AttachmentType } from '../../config';
import { SaveOutlined, WarningOutlined } from '@ant-design/icons';
import { TfiDownload } from 'react-icons/tfi';

import RoundedButton from '../Globle/RoundedButton';
import CustomTooltip from '../Globle/CustomTooltip';
import { STEP_TYPE } from '../../__utils/UiConst';
import { useState } from 'react';
import ClosetList from '../WorkSpace/mapping/ClosetList';
import UnitList from '../WorkSpace/mapping/UnitList';
export const PaginationButton = observer(() => {
    const {
        viewManager,
        closetManager,
        operationsManager,
        apiManager,
        drawingManager,
        matrixManager,
        uiManager,
    } = useMainContext();
    const [isLoading, setIsLoading] = useState(false);
    // Check if user is super admin
    const isDisabled = !viewManager.doUserHasAccess();

    const handleFinalize = async () => {
        if (viewManager.selectedPages.length === 0) {
            message.error('No pages selected');
            return;
        }
        closetManager.excludeClosets();
        viewManager.setSelectedMenu(2);
        try {
            viewManager.setHasUserFinalized(true);
            if (!viewManager.projectId) {
                try {
                    await viewManager.uploadPdf(closetManager.pdfBuffer);
                    closetManager.setSelectedPageNo(
                        viewManager.selectedPages[0].pageNum,
                    );
                    viewManager.createPageStatus();
                    try {
                        await viewManager.showPossibleClosets();
                    } catch (showClosetsError) {
                        console.error(
                            'Failed to show possible closets:',
                            showClosetsError.message,
                        );
                        throw showClosetsError;
                    }
                } catch (uploadError) {
                    console.error('Failed to upload PDF:', uploadError.message);
                    throw uploadError;
                }
            } else {
                await viewManager.updatePages();
                closetManager.setSelectedPageNo(
                    viewManager.selectedPages[0].pageNum,
                );
                viewManager.createPageStatus();
                try {
                    await viewManager.showPossibleClosets();
                } catch (showClosetsError) {
                    console.error(
                        'Failed to show possible closets:',
                        showClosetsError.message,
                    );
                    throw showClosetsError;
                }
            }
        } catch (generalError) {
            console.error(
                'An unexpected error occurred:',
                generalError.message,
            );
            message.error('An unexpected error occurred. Please try again.');
        }
    };

    const handleMatrixCalculation = async () => {
        drawingManager.handleDeselectAll();
        if (closetManager.units.length === 0) {
            message.error('No units present. Please add a unit first.');
            return;
        }
        const unitsWithoutClosets = closetManager.units.filter(
            (unit) =>
                !closetManager.closets.some(
                    (closet) => closet.unitId === unit.id,
                ),
        );

        if (unitsWithoutClosets.length > 0) {
            unitsWithoutClosets.forEach((unit) => {
                message.error(
                    `No closets present in the unit "${unit.name}". Please add a closet first.`,
                );
            });
            return;
        }
        if (closetManager.lengthValidation()) return;
        let flag = false;
        closetManager.closets.forEach((closet) => {
            if (
                !closetManager.attachments?.find(
                    (attachment) =>
                        attachment.closetId === closet.id &&
                        attachment._attachmentType === AttachmentType.DOOR,
                )
            ) {
                message.error(
                    `Please add a door to the ${closet.name} at page number ${closet.pageNo} and unit ${closetManager.unitNameById(closet.unitId)}.`,
                );
                flag = true;
            }
        });
        if (flag) return;
        closetManager.setIsLoading(true);
        viewManager.setSelectedMenu(3);
        uiManager.setSelectedStep(STEP_TYPE.MATRIX);
        await closetManager.fitToScreen();
        drawingManager.transformAnnotationCanvas();
        closetManager.clearClosetCanvas();
        await closetManager.getMatrixCalculationData();
        matrixManager.setUnitData(closetManager.units);
        matrixManager.setClosetData(closetManager.closets);
        matrixManager.processUnitData();
        viewManager.setMatrixCalculation(true);
        await matrixManager.setSelectedPageNo(1);
        closetManager.setIsLoading(false);
    };

    const handleSaveAndDone = async () => {
        if (!viewManager.superAdmin) return;
        if (!apiManager.validateAllUnits()) return;
        Modal.confirm({
            title: (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#1c1c1c',
                        paddingBottom: '4px',
                        borderBottom: '1px solid #cccccc',
                    }}>
                    <SaveOutlined
                        style={{ fontSize: '20px', color: '#1c1c1c' }}
                    />
                    Submit
                </div>
            ),

            icon: null, // Remove default icon since we're using custom title

            content: (
                <div
                    style={{
                        padding: '12px 0',
                        lineHeight: '1.6',
                    }}>
                    {/* Warning Section */}
                    <div
                        style={{
                            background: '#fff7e6',
                            border: '1px solid #ffd591',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                        }}>
                        <WarningOutlined
                            style={{
                                color: '#fa8c16',
                                fontSize: '16px',
                                marginTop: '2px',
                                flexShrink: 0,
                            }}
                        />
                        <div>
                            <div
                                style={{
                                    fontWeight: 600,
                                    color: '#d4380d',
                                    marginBottom: '8px',
                                }}>
                                Important: This Will Replace All Closets
                            </div>
                            <div style={{ color: '#8c8c8c', fontSize: '14px' }}>
                                This action cannot be undone. Please review
                                before proceeding.
                            </div>
                            <div style={{ color: '#8c8c8c', fontSize: '14px' }}>
                                Any custom modifications to current closets will
                                be permanently lost.
                            </div>
                        </div>
                    </div>
                </div>
            ),

            width: 480,
            centered: true,
            padding: '0px',

            okText: (
                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: 500,
                    }}>
                    <SaveOutlined />
                    Yes, Submit
                </span>
            ),

            cancelText: <span style={{ fontWeight: 500 }}>Cancel</span>,

            okButtonProps: {
                size: 'large',
                type: 'primary',
                className: 'confirm-modal-button',
                style: {
                    background: '#F46147',
                    border: 'none',
                    fontWeight: 500,
                    height: '40px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
            },

            cancelButtonProps: {
                size: 'large',
                className: 'confirm-modal-button',
                style: {
                    fontWeight: 500,
                    height: '40px',
                    borderRadius: '6px',
                    border: 'none',
                    color: '#1C1C1E',
                    backgroundColor: '#E5E5E5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
            },

            onOk: async () => {
                closetManager.setIsLoading(true);

                try {
                    const isSuccess =
                        await operationsManager.downloadClosetData();
                    if (!isSuccess) return;
                    if (!(await apiManager.clientSideSaveAPI())) {
                        message.error({
                            content: 'Failed to save project data',
                            duration: 4,
                            style: { marginTop: '20px' },
                        });
                    } else {
                        message.success({
                            content: 'Successfully uploaded project data',
                            duration: 4,
                            style: { marginTop: '20px' },
                        });
                    }
                } catch (error) {
                    message.error({
                        content:
                            'Failed to download closet data. Please try again.',
                        duration: 4,
                        style: { marginTop: '20px' },
                    });
                    console.error('Error during download:', error);
                } finally {
                    closetManager.setIsLoading(false);
                }
            },

            onCancel: () => {
                // Optional: Show a subtle message that the action was cancelled
            },

            styles: {
                padding: '0px',
            },

            // Mask closable for better UX
            maskClosable: false,

            // Keyboard support
            keyboard: true,
        });
    };

    const handleFinalizeLeft = async () => {
        viewManager.setSelectedMenu(2);
        uiManager.setSelectedStep(STEP_TYPE.MAPPING);
        viewManager.setMatrixCalculation(false);
        closetManager.setSelectedPageNo(viewManager.selectedPages[0].pageNum);
        message.error(
            'On drawing new units, Matrix Calculation will be reseted.',
        );
    };
    const rightSideBtns = [
        // {
        //     label: 'Finalize',
        //     icon: FaAngleRight,
        //     onClick: async () => {
        //         await handleFinalize();
        //     },
        //     isForward: true,
        //     menu: 1,
        // },
        // {
        //     label: 'Matrix Calculation',
        //     icon: FaAngleRight,
        //     onClick: async () => {
        //         if (viewManager.fullPdf) {
        //             await handleMatrixCalculation();
        //         } else {
        //             message.error('Full pdf is still not arrived');
        //         }
        //     },
        //     isForward: true,
        //     menu: 2,
        // },
        // {
        //     label: 'Save & Done',
        //     icon: FaRegCircleCheck,
        //     onClick: async () => {
        //         await handleSaveAndDone();
        //     },
        //     isForward: true,
        //     menu: 3,
        //     loading: closetManager.isLoading,
        //     disabled: !viewManager.superAdmin,
        // },
    ];
    const leftSideBtns = [
        // {
        //     label: 'Back to pages',
        //     icon: IoIosArrowBack,
        //     onClick: () => {
        //         drawingManager.handleDeselectAll();
        //         if (viewManager.fullPdf) {
        //             viewManager.setHasUserFinalized(false);
        //             // viewManager.setSelectedMenu(1);
        //             uiManager.setIsPdfUploadModel(true);
        //         } else {
        //             message.error('Full pdf is still not arrived');
        //         }
        //     },
        //     menu: 2,
        //     disabled: !viewManager.superAdmin,
        // },
        {
            label: 'Finalize',
            icon: IoIosArrowBack,
            onClick: async () => {
                await handleFinalizeLeft();
            },
            menu: 3,
        },
    ];

    const handleClick = async () => {
        if (isDisabled) return;
        closetManager.setIsSaving(true);
        await operationsManager.downloadClosetData();
        closetManager.setIsSaving(false);
    };

    return (
        <div>
            <div className="absolute bottom-[14px] right-[24px] flex gap-[8px] ">
                {viewManager.selectedMenu === 2 && (
                    <div className="h-[64px] rounded-[100px] p-[8px] bg-theme-white shadow flex justify-center items-center gap-[8px] z-50">
                        {/* <Button
                            label={'Save'}
                            icon={FaRegSave}
                            onClick={handleClick}
                            loading={closetManager.isSaving}
                            isForward={true}
                            disabled=
                            {isDisabled}
                        /> */}
                        <UnitList />
                        <ClosetList />
                        <CustomTooltip
                            label={'Download Annotated PDF'}
                            placement="top">
                            <RoundedButton
                                isLoading={isLoading}
                                className="w-[48px] h-[48px] group"
                                icon={
                                    <TfiDownload className="text-2xl group-hover:text-theme-white" />
                                }
                                onClick={async () => {
                                    setIsLoading(true);
                                    await closetManager.downloadAnnotatedPdf();
                                    setIsLoading(false);
                                }}
                            />
                        </CustomTooltip>
                        <Button
                            label={'Continue to Matrix'}
                            onClick={async () => {
                                if (viewManager.fullPdf) {
                                    await handleMatrixCalculation();
                                } else {
                                    message.error(
                                        'Full pdf is still not arrived',
                                    );
                                }
                            }}
                            className={
                                'bg-theme-orange text-theme-white hover:bg-theme-orange-hover'
                            }
                        />
                    </div>
                )}
                {viewManager.selectedMenu === 3 && (
                    <div className="h-[64px] rounded-[100px] p-[8px] bg-theme-white shadow flex justify-center items-center gap-[8px] z-50">
                        <Button
                            label={'Save & Done'}
                            onClick={async () => {
                                await handleSaveAndDone();
                            }}
                            className={
                                'bg-theme-orange text-theme-white hover:bg-theme-orange-hover'
                            }
                            isForward={true}
                            icon={FaRegCircleCheck}
                            loading={closetManager.isLoading}
                            disabled={!viewManager.superAdmin}
                        />
                    </div>
                )}
                {/* {rightSideBtns
                    .filter((btn) => viewManager.selectedMenu === btn.menu)
                    .map((btn) => (
                        <Button
                            key={btn.label}
                            label={btn.label}
                            icon={btn.icon}
                            onClick={btn.onClick}
                            isForward={btn.isForward}
                            menu={btn.menu}
                            loading={btn.loading}
                            disabled={btn.disabled}
                        />
                    ))} */}
            </div>
            {viewManager.selectedMenu === 3 && (
                <div className="absolute bottom-[14px] left-[24px] z-50 h-[64px] rounded-[100px] p-[8px] bg-theme-white shadow flex justify-center items-center gap-[8px] ">
                    {leftSideBtns
                        .filter((btn) => viewManager.selectedMenu === btn.menu)
                        .map((btn) => (
                            <Button
                                key={btn.label}
                                label={btn.label}
                                icon={btn.icon}
                                onClick={btn.onClick}
                                isForward={btn.isForward}
                                menu={btn.menu}
                                disabled={btn.disabled}
                                className={
                                    'bg-theme-orange text-theme-white hover:bg-theme-orange-hover'
                                }
                            />
                        ))}
                </div>
            )}
        </div>
    );
});
